/*
 * Two-column birthday spreadsheet parser. Accepts CSV and XLSX/XLS.
 * Returns one row per parsed birthday (name + month + day) plus a
 * list of rows that didn't parse so the teacher can see what we
 * skipped before importing.
 *
 * Date column accepts whatever the spreadsheet hands us:
 *   - JS Date object (Excel date cells from read-excel-file)
 *   - "5/10/2010", "05-10-2010"  (US MM/DD/YYYY)
 *   - "5/10", "05-10"            (US MM/DD)
 *   - "May 10", "May 10, 2010"   (textual)
 * Year is ignored — birthdays in v2 are stored as month + day only.
 *
 * First row is treated as a header (and skipped) if either cell
 * matches a likely header keyword. Otherwise every row is data.
 */

import { readSheet } from 'read-excel-file/browser';

export interface ParsedBirthday {
  /** 1-based row in the source file, for the preview UI. */
  row: number;
  name: string;
  month: number;
  day: number;
}

export interface ParseError {
  row: number;
  raw: string;
  reason: string;
}

export interface ParseResult {
  birthdays: ParsedBirthday[];
  errors: ParseError[];
}

const HEADER_KEYWORDS = new Set([
  'name',
  'student',
  'student name',
  'first name',
  'firstname',
  'birthday',
  'birthdate',
  'birth date',
  'dob',
  'date',
  'birth day',
]);

function looksLikeHeader(row: ReadonlyArray<unknown>): boolean {
  if (row.length === 0) return false;
  const cells = row
    .slice(0, 2)
    .map((c) => (typeof c === 'string' ? c.trim().toLowerCase() : null))
    .filter((c): c is string => !!c);
  return cells.some((c) => HEADER_KEYWORDS.has(c));
}

function parseDateCell(value: unknown): { month: number; day: number } | null {
  if (value === null || value === undefined) return null;

  // Native Date (Excel date cells) — use local date parts.
  if (value instanceof Date && !isNaN(value.getTime())) {
    return { month: value.getMonth() + 1, day: value.getDate() };
  }

  // Numeric (e.g. Excel cells parsed as plain numbers like 510 for "5/10").
  // Treat as a string and let the string branch take over.
  const str = String(value).trim();
  if (!str) return null;

  // Try slash- or dash-separated parts first: "5/10", "5/10/2010", "05-10".
  const partMatch = str.match(/^(\d{1,4})[/-](\d{1,4})(?:[/-](\d{1,4}))?$/);
  if (partMatch) {
    const a = Number(partMatch[1]);
    const b = Number(partMatch[2]);
    // Ambiguity: 2010-05-10 (ISO) vs 5/10/2010 (US). If first part is a
    // 4-digit year, swap to year-month-day; otherwise assume US MM/DD.
    if (partMatch[1] && partMatch[1].length === 4 && a > 1900) {
      // ISO YYYY-MM-DD
      const month = b;
      const day = Number(partMatch[3] ?? 0);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return { month, day };
      }
      return null;
    }
    // US MM/DD or MM/DD/YYYY
    const month = a;
    const day = b;
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { month, day };
    }
    return null;
  }

  // Textual fallback ("May 10", "May 10, 2010", "10 May", "2010-05-10").
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime()) && parsed.getFullYear() > 1900) {
    return { month: parsed.getMonth() + 1, day: parsed.getDate() };
  }

  return null;
}

function rowsToBirthdays(rows: ReadonlyArray<ReadonlyArray<unknown>>): ParseResult {
  const birthdays: ParsedBirthday[] = [];
  const errors: ParseError[] = [];

  const startIndex = rows.length > 0 && looksLikeHeader(rows[0]!) ? 1 : 0;

  for (let i = startIndex; i < rows.length; i++) {
    const row = rows[i]!;
    const rowNumber = i + 1; // 1-based for the user

    const rawName = row[0];
    const rawDate = row[1];

    const name =
      typeof rawName === 'string' ? rawName.trim() : rawName == null ? '' : String(rawName).trim();

    if (!name) {
      // Skip fully empty rows silently; flag rows with only a name or only a date.
      if (rawDate != null && String(rawDate).trim() !== '') {
        errors.push({
          row: rowNumber,
          raw: `${rawName ?? ''}, ${rawDate ?? ''}`,
          reason: 'Missing name',
        });
      }
      continue;
    }

    const md = parseDateCell(rawDate);
    if (!md) {
      errors.push({
        row: rowNumber,
        raw: `${name}, ${rawDate ?? ''}`,
        reason: 'Couldn’t read the birthday — expected M/D or M/D/YYYY.',
      });
      continue;
    }

    birthdays.push({ row: rowNumber, name, month: md.month, day: md.day });
  }

  return { birthdays, errors };
}

// ---------- CSV ----------

/** Tiny CSV parser. Handles quoted fields containing commas/newlines.
 *  Good enough for the shapes a teacher's spreadsheet export produces. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (ch === '\r') {
      // ignore — \r\n handled by \n branch, lone \r treated as line break
      if (text[i + 1] !== '\n') {
        row.push(cell);
        rows.push(row);
        row = [];
        cell = '';
      }
    } else {
      cell += ch;
    }
  }
  // Trailing cell / row
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  // Drop trailing empty rows
  while (rows.length > 0 && rows[rows.length - 1]!.every((c) => c.trim() === '')) {
    rows.pop();
  }
  return rows;
}

// ---------- Public API ----------

export async function parseBirthdayFile(file: File): Promise<ParseResult> {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
    const rows = await readSheet(file);
    return rowsToBirthdays(rows as ReadonlyArray<ReadonlyArray<unknown>>);
  }
  // Default: treat as CSV (covers .csv, .tsv, and anything plain-text).
  const text = await file.text();
  const rows = parseCsv(text);
  return rowsToBirthdays(rows);
}
